import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    EntryPoint,
    FieldType,
    HotelStandalonePaxRestrictionPropsDefault,
    RoomsProps,
    SlideType,
    TabTypeProp,
    TabTypePropDefault,
} from '../../../models';
import Field from '../../FlightsManager/MobileFields/Field';
import { usePaxSummaryText } from '../usePaxSummaryText';
import {
    getHotelStandaloneAdultsAndKids,
    hasHotelStandaloneKidsErrors,
    getRooms,
    canAddRoom,
} from '../../../redux/currentSearch/hotelStandalone.selectors';
import {
    FragmentBody,
    NextButtonWithValidation,
    Fragment,
    FieldHeaderPrisma,
} from '../../FlightsManager/MobileFragments';
import { SlideGroupContext } from '../../../components/SlideManager';
import ConnectedHSAPaxSelector from './HotelStandalonePaxSelector';
import {
    CMS,
    CurrentAccommodationSearchSessionStorage,
    HotelRoomUtils,
    TrackingUtils,
    withPageContext
} from '../../../utils';
import { getAddRoomSelectorClassNames } from '../useRoomSelectorClassNames';
import { addRoom } from '../../../redux/currentSearch/actions';
import { getActiveProduct } from '../../../redux/currentSearch/selectors';

const FIELD_NAME_TRACKING = 'guest';

const RoomSelector = ({
    rooms,
    pax,
    hasError,
    showAddRoom,
    addRoom,
    trackEvent,
    activeProduct,
}) => {
    const { t } = useTranslation();
    const errorMessage = hasError ? t('roomSelector.children.error') : '';
    const placeHolder = t('flightsManager.selection.pax.title');
    const [paxSummaryText] = usePaxSummaryText(pax, rooms.length);
    const { isOpenedWithEmptyFields, getCurrent } = useContext(SlideGroupContext);
    const currentSlideGroup = getCurrent();
    const isDone = currentSlideGroup.history.indexOf(SlideType.ROOM) > -1 || !isOpenedWithEmptyFields;
    const numPax = HotelRoomUtils.getTotalPax(rooms);
    const addRoomClass = getAddRoomSelectorClassNames(true);
    const kidsAgeRange = HotelRoomUtils.getKidsRange(HotelStandalonePaxRestrictionPropsDefault);
    const onClickAddRoom = () => {
        const newRoom = HotelRoomUtils.getNewRoom(rooms, HotelStandalonePaxRestrictionPropsDefault);
        addRoom(newRoom);
        CurrentAccommodationSearchSessionStorage.setRooms(newRoom);
    };

    const onEntered = useCallback(() => {
        if (activeProduct === EntryPoint.HOTELS) {
            const action = TrackingUtils.ACTION[activeProduct.toUpperCase()];
            const label = TrackingUtils.LABEL.fieldOpen(activeProduct, FIELD_NAME_TRACKING);
            trackEvent({ action, label });
            document.body.style.overflow = 'hidden';
        }
    }, [activeProduct]);

    const onExiting = useCallback(() => {
        if (activeProduct === EntryPoint.HOTELS) {
            document.body.style.removeProperty('overflow');
        }
    }, [activeProduct]);

    return (
        <>
            <Field
                icon="pax"
                id={FieldType.ROOM}
                value={paxSummaryText}
                errorMessage={errorMessage}
                placeholder={placeHolder}
            />
            <Fragment
                id={SlideType.ROOM}
                isDone={isDone}
                isEmpty={false}
                setValue={Function.prototype}
                onEntered={onEntered}
                onExiting={onExiting}
            >
                {() => (
                    <>
                        <FieldHeaderPrisma
                            textKey="flightsManager.selection.pax.title"
                        />
                        <FragmentBody padding={false}>
                            { rooms.map((room, index) => (
                                <ConnectedHSAPaxSelector
                                    room={room}
                                    roomNumber={index}
                                    key={room.id}
                                    numPax={numPax}
                                    hasError={hasError}
                                    showRoomHeader
                                    paxRestrictions={HotelRoomUtils.getRoomPaxRanges(room, HotelStandalonePaxRestrictionPropsDefault, numPax)}
                                    kidsAgeRange={kidsAgeRange}
                                />
                            )) }
                            { showAddRoom && (
                                <div className={addRoomClass.wrapper} onClick={onClickAddRoom} data-testid="add-room-selector">
                                    <span className="odf-icon odf-icon-plus odf-icon-s odf-text-action odf-space-inner-xs" />
                                    <span className="odf-link"><CMS id="roomSelector.button.addRoom" /></span>
                                </div>
                            )}
                        </FragmentBody>
                        <NextButtonWithValidation type={SlideType.ROOM} testId="room-selector-next-btn" />
                    </>
                )}
            </Fragment>
        </>
    );
};

RoomSelector.propTypes = {
    rooms: RoomsProps.isRequired,
    hasError: PropTypes.bool.isRequired,
    activeProduct: TabTypeProp,
    trackEvent: PropTypes.func.isRequired,
};

RoomSelector.defaultProps = {
    activeProduct: TabTypePropDefault,
};

const mapStateToPropsSelector = createStructuredSelector({
    rooms: getRooms,
    pax: getHotelStandaloneAdultsAndKids,
    hasError: hasHotelStandaloneKidsErrors,
    showAddRoom: canAddRoom,
    activeProduct: getActiveProduct,
});

export const HotelStandaloneRoomSelectorMobile = connect(mapStateToPropsSelector, {
    addRoom,
})(withPageContext(RoomSelector));
